/**
 * OfflineBanner - Shows when user is offline
 * 
 * Displays a persistent banner indicating offline status
 * and showing cached content warning.
 */
import React from 'react';
import { WifiOff, Wifi, X } from 'lucide-react';

export function OfflineBanner({ isOnline, wasOffline, onDismiss }) {
  // Don't show anything if online and wasn't recently offline
  if (isOnline && !wasOffline) {
    return null;
  }

  // Show "back online" message briefly
  if (isOnline && wasOffline) {
    return (
      <div className="bg-green-500 text-white px-4 py-2.5 flex items-center justify-center gap-2 text-sm font-medium animate-in slide-in-from-top duration-300">
        <Wifi className="w-4 h-4" />
        <span>You&apos;re back online</span>
      </div>
    );
  }

  // Show offline banner
  return (
    <div className="bg-amber-500 text-white px-4 py-2.5 flex items-center justify-between gap-3 text-sm">
      <div className="flex items-center gap-2 flex-1">
        <WifiOff className="w-4 h-4 flex-shrink-0" />
        <span className="font-medium">You&apos;re offline</span>
        <span className="hidden sm:inline text-amber-100">— showing cached content</span>
      </div>
      {onDismiss && (
        <button 
          onClick={onDismiss}
          className="text-white/80 hover:text-white p-1"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

/**
 * OfflineOverlay - Prevents interactions when offline
 * 
 * Shows a subtle overlay on interactive elements that
 * shouldn't work when offline (like posting, commenting).
 */
export function OfflineDisabledWrapper({ isOnline, children, message = "Available when online" }) {
  if (isOnline) {
    return <>{children}</>;
  }

  return (
    <div className="relative">
      <div className="opacity-50 pointer-events-none">
        {children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xs text-slate-500 dark:text-slate-400 bg-white/90 dark:bg-[#2d2f33]/90 px-2 py-1 rounded shadow-sm">
          {message}
        </span>
      </div>
    </div>
  );
}

export default OfflineBanner;
